import React, { useState, useMemo, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useInView } from 'react-intersection-observer'

import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { FlightsClassTypes, fareClassUrlParser } from 'constants/flight'
import { CHARTER_BUNDLE_DURATION_OPTIONS, HOTEL_OFFERS_PAGE_SIZE, MALDIVES_BUNDLE_DEFAULT_PRICE_PER_PERSON, MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF } from 'constants/charterFlight'
import HeroImage from 'content/components/HeroImage'
import Pill from 'LuxKit/Pill'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import Pane from 'components/Common/Pane'
import Modal from 'LuxKit/Modal/Modal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import TextLink from 'LuxKit/TextLink'
import config from 'constants/config'
import Group from 'components/utils/Group'
import LayoutContainer from 'components/Common/LayoutContainer'
import MaldivesCharterFilters from './MaldivesDirectSearchFilters'
import MaldivesDirectSearchFlightCalendar from './MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendar'
import OfferList, { OfferListAction } from 'components/OfferList/OfferList'
import useOfferList from 'hooks/Offers/useOfferList'
import useOffers from 'hooks/Offers/useOffers'
import HotelPriceToggle from 'components/Hotels/HotelPriceToggle'
import { pluralizeToString } from 'lib/string/pluralize'
import { parseAsBoolean, parseAsString, parseAsStringEnum, useQueryState } from 'nuqs'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { buildSuggestedDatesParamsKey, encodeChildrenOccupancy, parseOccupancyFromParams } from 'lib/search/searchUtils'
import { queryNullChildren } from 'constants/search'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import useFlightAndHotelBundle from 'hooks/FlightAndHotelBundle/useFlightAndHotelBundle'
import Places from 'constants/places'
import ReturnCharterFlightTile from 'components/Flights/CharterBundle/ReturnCharterFlightTile'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { setRecentlyUsedAirportCode } from 'actions/AuthActions'
import { OFFER_SET_SEARCH_RESULT_METADATA } from 'actions/actionConstants'
import { getDefaultAirport } from 'selectors/flightsSelectors'
import { MaldivesUspGroup1, MaldivesUspGroup2, MaldivesUspMobile, CharterPartnerIcon } from 'components/MaldivesCharter/MaldivesCharterUsp/MaldivesCharterUspItems'
import CharterBundleLandingPageUspItem from 'components/MaldivesCharter/MaldivesCharterUsp/CharterBundleLandingPageUspItem'
import { useFlightItemData } from './hooks/useFlightItemData'
import { useCalendarItemData } from './hooks/useCalendarItemData'
import { buildBundleOfferFilters } from './MaldivesDirectUtils'
import { filterDateRangesByNights } from './hooks/utils'
import FormatCurrency from 'components/Common/FormatCurrency'
import MaldivesDirectSearchFlightCalendarMobile from './MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendarMobile'
import { LUXURY_ESCAPES } from 'constants/brands'
import MaldivesCallPrompt from './MaldivesCallPrompt'
import useCharterDateRangesByMonth from 'hooks/CharterFlights/useCharterDateRangesByMonth'
import { EmptyArray } from 'lib/array/arrayUtils'
import CharterBundleCalendarActions from 'components/OfferPage/Filters/CharterFlight/CharterBundleCalendarActions'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import { MaldivesDirectBundleProvider } from './MaldivesDirectBundleContext'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import getOfferListKey from 'lib/offer/offerListKey'
import { useMaldivesOffersPricing, MaldivesOffersPricingContext } from './hooks/useMaldivesOffersPricing'
import { isSkyLoungeCompatible } from 'lib/flights/flightUtils'

type BundleType = '5-nights' | '12-nights'

const MELBOURNE_AIRPORT: App.Airport = {
  code: 'MEL',
  name: 'Tullamarine',
  latitude: -37.8136,
  longitude: 144.9631,
  countryCode: 'AU',
}

const StyledHeroImage = styled(HeroImage)`
  position: relative;
  min-height: ${rem(220)};
  justify-content: flex-start;

  /* Mobile: solid blue background with image at bottom */
  background-color: #0280B3;

  @media (max-width: 369px) {
    aspect-ratio: 3 / 2;
  }

  ${mediaQueryUp.tablet} {
    min-height: ${rem(400)};
    justify-content: center;
    background-color: transparent;
  }

  &.price-enabled {
    min-height: ${rem(264)};

    @media (max-width: 369px) {
      aspect-ratio: 7 / 6;
    }
  }

  /* Mobile: Position image at bottom portion only, overlap by 1px to avoid gap */
  > div:first-child {
    top: calc(40% - 1px);

    ${mediaQueryUp.tablet} {
      top: 0;
    }
  }

  /* Blue gradient overlay from Figma design */
  &::after {
    content: '';
    position: absolute;
    top: calc(40% - 1px);
    right: 0;
    bottom: 0;
    left: 0;
    pointer-events: none;
    background: linear-gradient(0deg, rgba(2, 128, 179, 0.00) 60.42%, #0280B3 89.63%);

    ${mediaQueryUp.tablet} {
      top: 0;
      background: linear-gradient(270deg, rgba(2, 128, 179, 0.00) 28.3%, rgba(2, 128, 179, 0.90) 89.24%);
    }
  }

  /* When price is enabled, push image lower on mobile to keep price in solid blue section */
  &.price-enabled {
    > div:first-child {
      top: calc(50% - 1px);

      ${mediaQueryUp.tablet} {
        top: 0;
      }
    }

    &::after {
      top: calc(50% - 1px);

      ${mediaQueryUp.tablet} {
        top: 0;
      }
    }
  }
`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: ${rem(1280)};
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(24)};
  align-items: start;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
    gap: ${rem(48)};
    margin-bottom: 0;
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: 1.5fr 1fr;
    gap: ${rem(64)};
  }
`

// Apply text shadow to all hero text for readability over background image
const HeroText = styled.div`
  & h1, & h2, & h3, & h4, & p, & span {
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
`

const CTACard = styled(Pane)`
  /* Pane already provides background-color via neutral-eight default */
  box-shadow: ${props => props.theme.shadow.bottom.small};
`

const CTACardMobile = styled(Pane)`
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translate(-50%, 50%);
  z-index: 1;
  min-width: ${rem(350)};
  box-shadow: ${props => props.theme.shadow.bottom.small};
`

// USP Section Styles
const USPSection = styled.section`
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};

  /* Add padding-top on mobile to accommodate overlapping CTACard */
  padding: ${rem(100)} 0 ${rem(32)} 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(32)} 0;
  }

  /* Control br visibility in section headings */
  br {
    display: block;

    ${mediaQueryUp.tablet} {
      display: none;
    }
  }

  /* Main heading: wrap on mobile, no wrap on tablet+ */
  > div > div > h2 {
    white-space: normal;

    ${mediaQueryUp.tablet} {
      white-space: nowrap;
    }
  }
`

const USPGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rem(24)} ${rem(16)};

  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(4, 1fr);
    gap: ${rem(32)};
  }
`

const USPFooter = styled(Group)`
  margin-top: ${rem(40)};
  text-align: center;
`

function MaldivesSupportPhoneNumber({ headingVariant = 'heading5' }: { headingVariant?: 'heading5' | 'heading3' }) {
  return (
    <CSSBreakpoint min="tablet">
      <TextLink
        href={MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF}
        startIcon={<LinePhoneIcon />}
        size="large"
        underline={false}
        block
      >
        <Heading variant={headingVariant} as="span">
          {config.MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER}
        </Heading>
      </TextLink>
    </CSSBreakpoint>
  )
}

// Offers Section Styles
const OffersSection = styled.section`
  padding: ${rem(32)} 0;
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

const OffersContainer = styled(LayoutContainer)`
  max-width: ${rem(1200)};
  padding-left: 0;
  padding-right: 0;

  ${mediaQueryUp.tablet} {
    padding-left: ${rem(20)};
    padding-right: ${rem(20)};
  }
`

const OffersHeader = styled(Group)`
  margin-bottom: ${rem(24)};

  ${mediaQueryDown.tablet} {
    padding-left: ${rem(20)};
    padding-right: ${rem(20)};
  }
`
const MaldivesUspsCombined = [...MaldivesUspGroup1, ...MaldivesUspGroup2]
const isMelbourneAirport = (airport?: App.Airport) => ['AVV', 'MBW', 'MEB', 'MELALL'].includes(airport?.code ?? 'MEL')

function MaldivesDirectSearchPage() {
  const currentRegionCode = useAppSelector(state => state.geo.currentRegionCode)
  const maldivesCharterEnabled = currentRegionCode === 'AU'
  const priceOnMaldivesLandingPageEnabled = !!useOptimizelyExperiment(OptimizelyExperiments.croPriceOnMaldivesLandingPage)
  const isDesktop = useScreenSizeUp('desktop')
  const dispatch = useAppDispatch()

  // Detect when user has scrolled past the hero banner
  const { ref: heroDetectorRef, inView: heroInView } = useInView({
    threshold: 0,
    initialInView: true,
  })

  // State to track if user has manually closed the prompt
  const [isCallPromptClosed, setIsCallPromptClosed] = useState(false)

  // Flight and Hotel bundle management
  const flightAndHotelBundle = useFlightAndHotelBundle()

  // Occupancy query params - encoded as two separate query params (adults, children)
  // Default: 2 adults, no children (encoded as "2" for adults, "none" for children)
  const [adultsParam, setAdultsParam] = useQueryState('adults', parseAsString.withDefault('2'))
  const [childrenParam, setChildrenParam] = useQueryState('children', parseAsString.withDefault(queryNullChildren))

  // Parse the query params into rooms array, ensuring teenagers field is included for consistency
  const rooms = useMemo<Array<App.Occupants>>(
    () => parseOccupancyFromParams(adultsParam, childrenParam).map(room => ({
      ...room,
      teenagers: room.teenagers ?? 0,
    })),
    [adultsParam, childrenParam]
  )

  // Helper function to update rooms (updates both query params)
  const setRooms = useCallback((newRooms: Array<App.Occupants>) => {
    const adultsString = newRooms.map(room => room.adults).join(',')
    const childrenString = newRooms.map(room => encodeChildrenOccupancy(room.childrenAge)).join(',')

    setAdultsParam(adultsString)
    setChildrenParam(childrenString)
  }, [setAdultsParam, setChildrenParam])

  // Airport query params - explicit string parsers, use recentlyUsedAirportCode if available
  const departureAirportOfUser = useAppSelector(getDefaultAirport)

  const isMelbourneDefaultAirport = isMelbourneAirport(departureAirportOfUser) || isAgentHubEnabled
  const [departureAirport, setDepartureAirport] = useState<App.Airport>(isMelbourneDefaultAirport ? MELBOURNE_AIRPORT : departureAirportOfUser ?? MELBOURNE_AIRPORT)

  // sync airport to redux when agent hub is enabled so offer pages inherit the correct airport
  useEffect(() => {
    if (isAgentHubEnabled && departureAirport.code === MELBOURNE_AIRPORT.code) {
      dispatch(setRecentlyUsedAirportCode(MELBOURNE_AIRPORT.code))
    }
  }, [departureAirport.code, dispatch])

  // Flight class - custom parser that maps URL values (e.g., 'economy-plus') to internal types
  const [fareClass, setFareClass] = useQueryState(
    'fareClass',
    fareClassUrlParser.withDefault(FlightsClassTypes.ECONOMY)
  )

  // Bundle selection - '5-nights' or '12-nights'
  const [selectedBundle, setSelectedBundle] = useQueryState(
    'bundle',
    parseAsStringEnum<BundleType>(['5-nights', '12-nights'] as const).withDefault('5-nights')
  )

  // LuxPlus toggle
  // Default to false if brand is not Luxury Escapes
  const [luxPlusEnabled, setLuxPlusEnabled] = useQueryState(
    'luxPlus',
    parseAsBoolean.withDefault(config.BRAND === LUXURY_ESCAPES)
  )

  // Price display mode state (persisted in URL)
  const [priceMode, setPriceMode] = useQueryState(
    'priceMode',
    parseAsStringEnum<'total' | 'perPerson'>(['total', 'perPerson'] as const).withDefault('perPerson')
  )

  const pageSize = HOTEL_OFFERS_PAGE_SIZE

  const [checkInDateParam, setCheckInDate] = useQueryState('checkIn', parseAsString)
  const [checkOutDateParam, setCheckOutDate] = useQueryState('checkOut', parseAsString)

  const checkInDate = checkInDateParam ?? undefined
  const checkOutDate = checkOutDateParam ?? undefined

  // Flight calendar modal state
  const [isFlightCalendarOpen, setIsFlightCalendarOpen] = useState(false)

  const [selectedMonth, setSelectedMonth] = useState<string>('')

  // Handle price mode change
  function handlePriceModeChange(newMode: 'total' | 'perPerson') {
    setPriceMode(newMode)
  }

  // Handle flight calendar modal
  function handleOpenFlightCalendar() {
    setIsFlightCalendarOpen(true)
  }

  function handleCloseFlightCalendar() {
    setIsFlightCalendarOpen(false)
  }

  function handleBundleChange(bundle: BundleType) {
    setSelectedBundle(bundle)
    // Update dates based on bundle selection
    const nights = bundle === '5-nights' ? 5 : 12
    const newCheckoutDate = moment(checkInDate).add(nights, 'days').format(ISO_DATE_FORMAT)
    setCheckOutDate(newCheckoutDate)
  }

  function handleAirportChange(airport: App.Airport) {
    setDepartureAirport(airport)
    dispatch(setRecentlyUsedAirportCode(airport.code))
  }

  function handleFlightClassChange(flightClass: FlightsClassTypes) {
    setFareClass(flightClass)
  }

  // Check SkyLounge compatibility when rooms are applied (not on every intermediate change)
  const checkAndResetFareClassIfNeeded = useCallback((appliedRooms: Array<App.Occupants>) => {
    if (fareClass === FlightsClassTypes.LUXROW && !isSkyLoungeCompatible(appliedRooms)) {
      setFareClass(FlightsClassTypes.ECONOMY)
    }
  }, [fareClass, setFareClass])

  function handleLuxPlusToggle(enabled: boolean) {
    setLuxPlusEnabled(enabled)
  }

  function handleDateSelect(departDate: string, returnDate: string) {
    setCheckInDate(departDate)
    setCheckOutDate(returnDate)
  }

  // Handle flight calendar date selection (from modal)
  function handleFlightDateSelect(option: { departDate: string, returnDate: string, checkIn: string, checkOut: string }) {
    // Use checkIn and checkOut (inner dates) for hotel queries
    setCheckInDate(option.checkIn)
    setCheckOutDate(option.checkOut)
    handleCloseFlightCalendar()
  }

  function handleMonthChange(month: string) {
    setSelectedMonth(month)
  }

  // Handle duration change from flight calendar
  function handleDurationChangeFromCalendar(nights: number) {
    const bundle = nights === 5 ? '5-nights' : '12-nights'
    setSelectedBundle(bundle)

    // Update checkout date based on new duration
    const newCheckoutDate = moment(checkInDate).add(nights, 'days').format(ISO_DATE_FORMAT)
    setCheckOutDate(newCheckoutDate)
  }

  // Calculate nights based on selected bundle
  const nights = selectedBundle === '5-nights' ? 5 : 12

  // Get charter flight dates (departDate/returnDate) from dateRangesByMonth
  const { departDate, returnDate } = useCharterFlightDates({
    checkInDate,
    nights,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    rooms,
    fareClass,
  })

  const bundleFilters = useMemo<App.OfferListFilters>(() => {
    const filters = buildBundleOfferFilters({
      destinationId: Places.Maldives.id,
      destinationName: Places.Maldives.name,
      checkIn: checkInDate ?? '', // Light search ignores these anyway
      checkOut: checkOutDate ?? '',
      rooms: rooms,
      fareClass,
    })
    return {
      ...filters,
      isStream: false,
      limit: pageSize,
      // Only include departure airport if not Melbourne (default) to keep URLs clean
      departureAirportCode: departureAirport.code !== MELBOURNE_AIRPORT.code ? departureAirport.code : undefined,
    }
  }, [checkInDate, checkOutDate, rooms, fareClass, pageSize, departureAirport.code])

  const bundleContextValue = useMemo(() => ({
    charterBundle: true,
    isPricePerPerson: priceMode === 'perPerson',
    fareClass,
  }), [priceMode, fareClass])

  const maldivesOfferList = useOfferList(bundleFilters, {
    minRequiredOffers: pageSize,
    clientCheckAvailability: true,
  })
  const [offers, offersFetching] = useOffers<App.HotelOffer>(maldivesOfferList.offerIds)
  const isOffersLoading = useMemo(() => maldivesOfferList.fetching || offersFetching, [maldivesOfferList.fetching, offersFetching])
  const listKey = useMemo(() => bundleFilters ? getOfferListKey(bundleFilters) : '', [bundleFilters])
  const rawOfferListFromState = useAppSelector(state => listKey ? state.offer.offerLists[listKey] : undefined)
  const resolvedEmpty = useMemo(
    () => !isOffersLoading && rawOfferListFromState?.fetching === false && (rawOfferListFromState?.offerCount === 0),
    [isOffersLoading, rawOfferListFromState?.fetching, rawOfferListFromState?.offerCount]
  )
  const [lastNonEmptyOfferList, setLastNonEmptyOfferList] = useState<App.OfferList | null>(null)
  useEffect(() => {
    if (!maldivesOfferList.fetching && maldivesOfferList.offerIds.length > 0) {
      setLastNonEmptyOfferList(maldivesOfferList)
    }
  }, [maldivesOfferList, maldivesOfferList.fetching, maldivesOfferList.offerIds])

  const bundleData = useCalendarItemData({
    checkIn: checkInDate ?? '',
    checkOut: checkOutDate ?? '',
    optimizeForCheapest: true,
    nights,
    rooms: rooms ?? [],
    fareClass,
    priceMode,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
  })

  // Extract cheapest bundle price per person (prefer member price if available)
  const cheapestBundlePricePerPerson = bundleData.available
    ? (bundleData.memberPricePerPerson ?? bundleData.pricePerPerson)
    : undefined

  // Update Redux suggested dates when check-in/check-out changes so offer URLs use correct dates
  useEffect(() => {
    if (!checkInDate || !checkOutDate || maldivesOfferList.offerIds.length === 0) {
      return
    }

    const suggestedDatesKey = buildSuggestedDatesParamsKey('', '', rooms)
    const offerSuggestedDates = maldivesOfferList.offerIds.reduce<Record<string, App.OfferSuggestedDates>>((acc, offerId) => {
      acc[offerId] = {
        checkIn: checkInDate,
        checkOut: checkOutDate,
      }
      return acc
    }, {})

    // Use existing OFFER_SET_SEARCH_RESULT_METADATA action to update suggested dates
    // Using unique keys to avoid conflicts with other pages:
    // - searchTargetId: Use a unique prefix to avoid overwriting other pages' data
    // - Empty objects for other fields won't overwrite existing data due to reducer merge logic
    const uniqueSearchTargetId = `maldives-direct-${Places.Maldives.id}`
    const uniqueAvailabilityKey = `maldives-direct-${suggestedDatesKey}`
    const uniqueOfferMetaDataKey = `maldives-direct-${maldivesOfferList.key || ''}`

    dispatch({
      type: OFFER_SET_SEARCH_RESULT_METADATA,
      data: {
        distanceFromSearchTarget: {
          [uniqueSearchTargetId]: {},
        },
        offerAvailabilityFromSearchTarget: {
          [uniqueAvailabilityKey]: {},
        },
        offerSuggestedDates: {
          [suggestedDatesKey]: offerSuggestedDates,
        },
        offerMetaData: {
          [uniqueOfferMetaDataKey]: {},
        },
      },
      searchTargetId: uniqueSearchTargetId,
      availabilityKey: uniqueAvailabilityKey,
      suggestedDatesKey,
      offerMetaDataKey: uniqueOfferMetaDataKey,
    })
  }, [dispatch, checkInDate, checkOutDate, maldivesOfferList.offerIds, maldivesOfferList.key, rooms])

  const mainFlightData = useFlightItemData({
    checkIn: checkInDate ?? '',
    checkOut: checkOutDate ?? '',
    rooms,
    fareClass,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
  })

  // Get dateRangesByMonth from charter flight journeys API
  // Use fixed date range to fetch all available flights, then extract all dates from the response
  const { dateRangesByMonth: allDateRangesByMonth } = useCharterDateRangesByMonth({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    occupancies: rooms ?? EmptyArray,
    fareClass,
    disabled: (rooms ?? EmptyArray).length === 0,
  })

  // Filter dateRangesByMonth by selected nights
  const dateRangesByMonth = useMemo(
    () => filterDateRangesByNights(allDateRangesByMonth, nights),
    [allDateRangesByMonth, nights]
  )

  useEffect(() => {
    if (checkInDate) {
      setSelectedMonth(moment(checkInDate).format('MMM'))
    } else {
      const months = Object.keys(dateRangesByMonth)
      if (months.length > 0) {
        setSelectedMonth(months[0])
      }
    }
  }, [checkInDate, dateRangesByMonth])

  // Calculate per-offer pricing using calendar + flight data
  const offersPricingData = useMaldivesOffersPricing({
    offers,
    rooms,
    dateRangesByMonth,
    fareClass,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    selectedCheckIn: checkInDate ?? undefined,
    selectedCheckOut: checkOutDate ?? undefined,
    nights,
  })

  // Sort offers by availability first (available before unavailable), then by price
  const sortedOfferIds = useMemo(() => {
    const { pricingByOfferId } = offersPricingData

    if (maldivesOfferList.fetching || offersFetching || offers.length === 0) {
      return maldivesOfferList.offerIds
    }

    const sorted = [...maldivesOfferList.offerIds].sort((a, b) => {
      const pricingA = pricingByOfferId[a]
      const pricingB = pricingByOfferId[b]

      const availableA = pricingA?.hasAvailability ?? false
      const availableB = pricingB?.hasAvailability ?? false

      if (availableA !== availableB) {
        return availableA ? -1 : 1
      }

      const priceA = pricingA?.bundleTotalPrice ?? Number.MAX_VALUE
      const priceB = pricingB?.bundleTotalPrice ?? Number.MAX_VALUE

      return priceA - priceB
    })

    return sorted
  }, [maldivesOfferList.offerIds, maldivesOfferList.fetching, offersFetching, offers, offersPricingData])

  const displayOfferList = useMemo<App.OfferList>(() => {
    if (resolvedEmpty) return maldivesOfferList
    if (sortedOfferIds.length > 0) {
      return {
        ...maldivesOfferList,
        offerIds: sortedOfferIds,
      }
    }
    return lastNonEmptyOfferList ?? maldivesOfferList
  }, [resolvedEmpty, maldivesOfferList, sortedOfferIds, lastNonEmptyOfferList])

  const offersHeaderNode = useMemo(() => {
    if (isOffersLoading) {
      return <BodyText variant="large">Loading offers…</BodyText>
    }
    return (
      <BodyText variant="large">
        Showing {pluralizeToString('offer', displayOfferList.offerIds.length)}
      </BodyText>
    )
  }, [isOffersLoading, displayOfferList.offerIds.length])

  // Sync bundle data to Redux store (only when dates are selected)
  useEffect(() => {
    if (checkInDate && checkOutDate) {
      flightAndHotelBundle.updateSelectedDates({
        checkIn: checkInDate,
        checkOut: checkOutDate,
      })
    }

    flightAndHotelBundle.updateFlightData({
      fetching: mainFlightData.fetching,
      totalPrice: mainFlightData.totalFlightFare,
      journey: mainFlightData.departingFlight,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInDate, checkOutDate, mainFlightData])

  if (!maldivesCharterEnabled) {
    // show 404 error page
    return <Error404PageWithOffers />
  }

  return (
    <div>
      <div ref={heroDetectorRef}>
        <StyledHeroImage
          id="maldives-charter-landing-banner"
          fit="center"
          gravity="center"
          mobileAspectRatio="7:6"
          tabletAspectRatio="9:5"
          desktopAspectRatio="18:5"
          largeDesktopAspectRatio="10:3"
          className={priceOnMaldivesLandingPageEnabled ? 'price-enabled' : undefined}
        >
          <HeroContent>
            <HeroText>
              <Group direction="vertical" gap={8} tabletGap={24}>
                <Pill kind="primary" variant="success" style={{ alignSelf: 'flex-start' }}>
                  Exclusive to Luxury Escapes
                </Pill>

                <VerticalSpacer gap={8}>
                  <CSSBreakpoint max="mobile">
                    <Heading variant="heading2" as="h1" colour="neutral-eight">
                      Australia's <em>first & only</em>
                      <br />direct Maldives flight
                    </Heading>
                  </CSSBreakpoint>
                  <CSSBreakpoint min="tablet">
                    <Heading variant="heading1" as="h1" colour="neutral-eight">
                      Australia's <em>first & only</em>
                      <br />direct Maldives flight
                    </Heading>
                  </CSSBreakpoint>

                  {priceOnMaldivesLandingPageEnabled && (
                    <>
                      <CSSBreakpoint min="tablet">
                        <BodyText variant="large" colour="neutral-eight">
                          Flights + {nights} night all-inclusive stay from <strong> <FormatCurrency value={cheapestBundlePricePerPerson !== undefined ? cheapestBundlePricePerPerson : MALDIVES_BUNDLE_DEFAULT_PRICE_PER_PERSON} format="roundedDollar" />/person*</strong>
                        </BodyText>
                      </CSSBreakpoint>
                      <CSSBreakpoint max="mobile">
                        <BodyText variant="medium" colour="neutral-eight">
                          Flights + {nights} night all-inclusive stay from <strong> <FormatCurrency value={cheapestBundlePricePerPerson !== undefined ? cheapestBundlePricePerPerson : MALDIVES_BUNDLE_DEFAULT_PRICE_PER_PERSON} format="roundedDollar" />/person*</strong>
                        </BodyText>
                      </CSSBreakpoint>
                    </>
                  )}
                </VerticalSpacer>
              </Group>
            </HeroText>

            <CSSBreakpoint min="tablet">
              <CTACard padding={20}>
                <Group direction="vertical" gap={8} tabletGap={16}>
                  <Group direction="vertical" gap={8} horizontalAlign="center">
                    <Group direction="vertical" gap={8} horizontalAlign="center" desktopDirection="horizontal">
                      <Heading variant="heading4" as="h4" align="center" colour="highlight-secondary">
                        Hurry, limited seats!
                      </Heading>
                      <Heading variant="heading4" as="h4" align="center" colour="neutral-one">
                        Call or book online.
                      </Heading>
                    </Group>

                    <Group direction="vertical" gap={0} horizontalAlign="center">
                      <BodyText variant="large" align="center">
                        Talk to a Maldives travel expert today.
                      </BodyText>
                      <BodyText variant="large" align="center" weight="bold">
                        Call our priority phone line
                      </BodyText>
                    </Group>

                    <MaldivesSupportPhoneNumber headingVariant="heading3" />
                  </Group>

                  <TextButton kind="primary" size="large" fit="full-width" href="#maldives-direct-search-filters-heading">
                    Browse all offers
                  </TextButton>
                </Group>
              </CTACard>
            </CSSBreakpoint>
          </HeroContent>

          <CSSBreakpoint max="mobile">
            <CTACardMobile padding={20}>
              <Group direction="vertical" gap={8} tabletGap={16}>
                <Group direction="vertical" gap={12} horizontalAlign="center">
                  <Heading variant="heading4" as="h4" colour="highlight-secondary">
                    <BodyText variant="large" as="span" colour="highlight-secondary">
                      Hurry, limited seats!{' '}
                    </BodyText>
                    <BodyText variant="large" as="span" colour="neutral-one">
                      Call or book online.
                    </BodyText>
                  </Heading>
                  <Group direction="horizontal" gap={8} horizontalAlign="stretch" fullWidth verticalAlign="center">
                    <TextButton
                      kind="secondary"
                      variant="dark"
                      startIcon={<LinePhoneIcon />}
                      href={MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF}
                      rel="nofollow"
                      fit="full-width"
                    >
                      Call now
                    </TextButton>
                    <TextButton kind="primary" fit="full-width" href="#maldives-direct-search-filters-heading">
                      Browse all offers
                    </TextButton>
                  </Group>
                </Group>
              </Group>
            </CTACardMobile>
          </CSSBreakpoint>
        </StyledHeroImage>
      </div>

      {/* USP Section */}
      <USPSection>
        <CSSBreakpoint min="tablet">
          <LayoutContainer>
            <VerticalSpacer gap={40}>
              <Heading variant="heading2" as="h2" align="start" tabletAlign="center">
                Less time flying, <br /> more time in paradise
              </Heading>

              <USPGrid>
                {MaldivesUspsCombined.map(uspItem => (
                  <CharterBundleLandingPageUspItem
                    key={uspItem.title}
                    title={uspItem.title}
                    description={uspItem.description}
                    icon={uspItem.icon}
                    tabletIcon={uspItem.tabletIcon}
                  />)
                )}
              </USPGrid>

              <USPFooter direction="vertical" gap={8} verticalAlign="center" tabletDirection="horizontal" tabletGap={16} tabletHorizontalAlign="center">
                <BodyText variant="large">
                  Our Maldives travel experts can help with any questions. <strong>Call our priority line</strong>
                </BodyText>
                <MaldivesSupportPhoneNumber />
              </USPFooter>
            </VerticalSpacer>
          </LayoutContainer>
        </CSSBreakpoint>

        <CSSBreakpoint max="mobile">
          <LayoutContainer>
            <VerticalSpacer gap={16}>
              <Heading variant="heading3" as="h3" align="start" tabletAlign="center">
                Less time flying, <br /> more time in paradise
              </Heading>

              <Group direction="vertical" gap={4}>
                {MaldivesUspMobile.map(uspItem => (
                  <BodyTextBlock variant="medium" key={uspItem.title} startIcon={uspItem.icon} colour="neutral-one">
                    {uspItem.description}
                  </BodyTextBlock>
                ))}
              </Group>

              <CharterPartnerIcon />
            </VerticalSpacer>
          </LayoutContainer>
        </CSSBreakpoint>
      </USPSection>

      {/* Bundle Search Section */}
      <MaldivesCharterFilters
        searchState={{
          rooms,
          selectedBundle,
          departureAirport: departureAirport,
          flightClass: fareClass,
          luxPlusEnabled,
          checkInDate,
          checkOutDate,
          departDate,
          returnDate,
        }}
        onRoomsChange={setRooms}
        onRoomsApply={checkAndResetFareClassIfNeeded}
        onBundleChange={handleBundleChange}
        onDateSelect={handleDateSelect}
        onAirportChange={handleAirportChange}
        onFlightClassChange={handleFlightClassChange}
        onOpenFlightCalendar={handleOpenFlightCalendar}
      />

      {/* Offers Section - using same data flow as HotelSearchPage */}
      <OffersSection>
        <OffersContainer>
          <VerticalSpacer gap={32}>
            <ReturnCharterFlightTile
              route={MALDIVES_CHARTER_ROUTE}
              fareClass={fareClass}
              checkInDate={checkInDate}
              checkOutDate={checkOutDate}
              occupancies={rooms}
              flightOrigin={departureAirport.code === MELBOURNE_AIRPORT.code ? undefined : departureAirport}
            />
            <OffersHeader direction="vertical" gap={0} tabletDirection="horizontal" tabletVerticalAlign="center" tabletHorizontalAlign="space-between">
              {offersHeaderNode}
              <HotelPriceToggle value={priceMode} onChange={handlePriceModeChange} />
            </OffersHeader>
          </VerticalSpacer>

          <MaldivesOffersPricingContext.Provider value={offersPricingData}>
            <MaldivesDirectBundleProvider value={bundleContextValue}>
              <OfferList
                filters={bundleFilters}
                tileStyle="search"
                pageSize={pageSize}
                showLoadingTiles={isOffersLoading || displayOfferList.offerIds.length === 0}
                disableUseOfferList
                extraOfferListAndAction={{ action: OfferListAction.OVERRIDE, offerList: displayOfferList }}
                overrideFetching={isOffersLoading}
                minRequiredOffers={pageSize}
              />
            </MaldivesDirectBundleProvider>
          </MaldivesOffersPricingContext.Provider>
        </OffersContainer>
      </OffersSection>

      {/* Contact Information Banner */}
      <LayoutContainer>
        <Group direction="vertical" gap={12}>
          <Group direction="vertical" tabletDirection="horizontal" tabletHorizontalAlign="center" verticalAlign="center" gap={16}>
            <BodyText variant="large">
              Our Maldives travel experts can help with any questions
            </BodyText>
            <MaldivesSupportPhoneNumber />
          </Group>
          <BodyText variant="small" colour="neutral-two">
            *Based on average time savings compared with non-direct flights from Melbourne to Malé operated by competitor airlines. Calculation includes estimated total door-to-door travel time, encompassing flight duration, transfers, layovers and hotel check-in/check-out times.
          </BodyText>
        </Group>
      </LayoutContainer>

      {/* Flight Calendar Modal - Drawer on mobile, Default on tablet+ */}
      {/* Desktop: Show full calendar in modal */}
      {isDesktop && (
        <Modal
          isOpen={isFlightCalendarOpen}
          onClose={handleCloseFlightCalendar}
          title={
            <Group direction="horizontal" horizontalAlign="space-between" gap={16} verticalAlign="center" fullWidth>
              <span>Select your trip dates</span>
              <CharterBundleCalendarActions
                nights={nights}
                onDurationChange={handleDurationChangeFromCalendar}
                durationOptions={CHARTER_BUNDLE_DURATION_OPTIONS}
                luxPlusEnabled={luxPlusEnabled}
                onLuxPlusToggle={handleLuxPlusToggle}
                fareClass={fareClass}
                onFareClassChange={handleFlightClassChange}
              />
            </Group>
          }
          size="XL"
        >
          <MaldivesDirectSearchFlightCalendar
            onDateSelect={handleFlightDateSelect}
            nights={nights}
            occupancies={rooms}
            luxPlusEnabled={luxPlusEnabled}
            dateRangesByMonth={dateRangesByMonth}
            selectedMonth={selectedMonth}
            onMonthChange={handleMonthChange}
            fareClass={fareClass}
            priceMode={priceMode}
            selectedOptionId={`${checkInDate}-${nights}n`}
            mode="search"
            itemBundleDataByDate={offersPricingData.calendarPricingByDate}
          />
        </Modal>
      )}

      {/* Mobile: Show two-step calendar (month selection → date selection) */}
      {!isDesktop && (
        <Modal
          height="full"
          title="Select your trip dates"
          isOpen={isFlightCalendarOpen}
          onClose={handleCloseFlightCalendar}
          isPrimaryActionHidden
          flushListItems
        >
          <MaldivesDirectSearchFlightCalendarMobile
            onDateSelect={handleFlightDateSelect}
            nights={nights}
            occupancies={rooms}
            luxPlusEnabled={luxPlusEnabled}
            dateRangesByMonth={dateRangesByMonth}
            selectedMonth={selectedMonth}
            onMonthChange={handleMonthChange}
            fareClass={fareClass}
            priceMode={priceMode}
            selectedOptionId={`${checkInDate}-${nights}n`}
            mode="search"
            itemBundleDataByDate={offersPricingData.calendarPricingByDate}
            headerActions={
              <CharterBundleCalendarActions
                nights={nights}
                onDurationChange={handleDurationChangeFromCalendar}
                durationOptions={CHARTER_BUNDLE_DURATION_OPTIONS}
                luxPlusEnabled={luxPlusEnabled}
                onLuxPlusToggle={handleLuxPlusToggle}
                fareClass={fareClass}
                onFareClassChange={handleFlightClassChange}
              />
            }
          />
        </Modal>
      )}

      {/* Floating Call Prompt - appears when hero is out of view */}
      <MaldivesCallPrompt
        show={!heroInView && !isCallPromptClosed}
        onClose={() => setIsCallPromptClosed(true)}
      />
    </div>
  )
}

export default MaldivesDirectSearchPage
